import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { RefreshCw, CheckCircle, Info } from "lucide-react";
import { getAddress, isAddress, parseEther } from "viem";
import { useSignMessage } from "wagmi";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { C } from "./profileTheme";
import { InfoTip } from "./InfoTip";
import {
  formatEtherWithDecimals,
  formatNumberInput,
  isValidAddress,
} from "@/lib/utils";
import { showError } from "@/lib/toast";
import { useTranslation } from "react-i18next";
import { useBalanceV2 } from "@/hooks/api/useBalanceV2";
import { useTransferFusd } from "@/hooks/api/useTransferFusd";
import { CCC_AIF_ADDRESS, USDT_ADDRESS } from "@/config/env";
import { TransferFusdResultCode } from "@/api/types";
import { parseChainError } from "@/lib/chainError";
import { getTokenName, INTEGRAL_COIN_CONTRACT } from "@/config/common";

/** 与接口、余额列表中的 coinContract 一致（地址或 INTEGRAL_COIN_CONTRACT） */
function sameCoinKey(a: string, b: string): boolean {
  if (a === b) return true;
  if (!isAddress(a) || !isAddress(b)) return false;
  try {
    return getAddress(a) === getAddress(b);
  } catch {
    return false;
  }
}

export function TransferDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const { getTokenBalanceData } = useBalanceV2();
  const { t } = useTranslation();
  const { signMessageAsync } = useSignMessage();
  const transferFusd = useTransferFusd();

  const selectableCoins = (
    [USDT_ADDRESS, CCC_AIF_ADDRESS, INTEGRAL_COIN_CONTRACT] as const
  ).filter((x): x is string => Boolean(x));

  const defaultCoin = selectableCoins[0] ?? "";

  const [selectedCoin, setSelectedCoin] = useState<string>(defaultCoin);
  const [amount, setAmount] = useState("");
  const [toAddr, setToAddr] = useState("");
  const [step, setStep] = useState<"form" | "pending" | "success">("form");

  const tokenLabel = getTokenName(selectedCoin) || selectedCoin;

  const getBalance = (coinContract: string): number => {
    if (!coinContract) return 0;
    return +formatEtherWithDecimals(
      BigInt(getTokenBalanceData(coinContract)?.total ?? "0")
    );
  };
  const getTransferableBalance = (coinContract: string): number =>
    getBalance(coinContract);

  const getInfoText = (coinContract: string): string => {
    if (coinContract === CCC_AIF_ADDRESS) {
      return t("profilePage.transferDialog.aifTeamOnly", {
        tokenNameAif: getTokenName(CCC_AIF_ADDRESS),
      });
    }
    if (coinContract === INTEGRAL_COIN_CONTRACT) {
      return t("profilePage.transferDialog.aifxTeamOnly", {
        tokenNameAifx: getTokenName(INTEGRAL_COIN_CONTRACT),
      });
    }
    return "";
  };

  const getFeeRate = (_coinContract: string): number => 0.05;

  const numAmount = parseFloat(amount) || 0;
  const currentBalance = getBalance(selectedCoin);
  const transferableBalance = getTransferableBalance(selectedCoin);
  const feeRate = getFeeRate(selectedCoin);
  const fee = numAmount * feeRate;
  const receivedAmount = Math.max(numAmount - fee, 0);
  const requiresReceiverTeamWarn = selectedCoin !== USDT_ADDRESS;

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value && !/^\d*\.?\d*$/.test(value)) return;
    const [formatted, isGtMax] = formatNumberInput(value, transferableBalance);
    setAmount(formatted);
    if (isGtMax) showError(t("profilePage.transferDialog.exceedBalance"));
  };

  const handleClose = () => {
    setStep("form");
    setSelectedCoin(defaultCoin);
    setAmount("");
    setToAddr("");
    onClose();
  };

  const handleConfirm = async () => {
    const coinContract = selectedCoin;
    if (!coinContract) {
      showError(t("profilePage.transferDialog.unsupportedAsset"));
      return;
    }
    const trimmedAddr = toAddr.trim();
    if (!isValidAddress(trimmedAddr)) {
      showError(t("toast.chainError.invalidRecipient"));
      return;
    }

    let amountWei: bigint;
    try {
      amountWei = parseEther(amount.trim() as `${number}`);
    } catch {
      showError(t("profilePage.transferDialog.exceedBalance"));
      return;
    }
    if (amountWei <= 0n) return;

    const checksummedTo = getAddress(trimmedAddr);
    const transferTick = Date.now();

    const message = `${amountWei.toString()}${coinContract.toLowerCase()}${checksummedTo.toLowerCase()}${transferTick}`;
    setStep("pending");
    let signature: `0x${string}`;
    try {
      signature = (await signMessageAsync({ message })) as `0x${string}`;
    } catch (err) {
      setStep("form");
      showError(t(parseChainError(err)));
      return;
    }

    try {
      const data = await transferFusd.mutateAsync({
        amount: amountWei.toString(),
        coinContract,
        toAddress: checksummedTo,
        transferTick,
        signature,
      });
      if (data.resultCode === TransferFusdResultCode.Success) {
        setStep("success");
      } else {
        setStep("form");
      }
    } catch {
      setStep("form");
    }
  };

  const confirmDisabled =
    !amount ||
    numAmount <= 0 ||
    numAmount > transferableBalance ||
    !toAddr.trim() ||
    !selectedCoin ||
    transferFusd.isPending;

  return (
    <Dialog open={open} onOpenChange={open => !open && handleClose()}>
      <DialogContent
        className="max-w-sm mx-auto"
        style={{
          background: "#0d0f14",
          border: "1px solid rgba(34,197,94,0.20)",
          color: C.text,
        }}
      >
        <DialogHeader>
          <DialogTitle style={{ color: C.primary }}>
            {t("profilePage.transferDialog.title")}
          </DialogTitle>
        </DialogHeader>
        <AnimatePresence mode="wait">
          {step === "form" && (
            <motion.div
              key="form"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="space-y-4"
            >
              <div>
                <p className="text-xs mb-2" style={{ color: C.dim }}>
                  {t("profilePage.transferDialog.selectAsset")}
                </p>
                <div className="grid grid-cols-3 gap-2">
                  {selectableCoins.map(coin => (
                    <button
                      key={coin}
                      type="button"
                      onClick={() => setSelectedCoin(coin)}
                      className="py-2 rounded-lg text-sm font-semibold transition-all"
                      style={{
                        background: sameCoinKey(selectedCoin, coin)
                          ? "rgba(34,197,94,0.12)"
                          : C.card,
                        border: `1px solid ${sameCoinKey(selectedCoin, coin) ? "rgba(34,197,94,0.40)" : C.border}`,
                        color: sameCoinKey(selectedCoin, coin)
                          ? C.primary
                          : C.muted,
                      }}
                    >
                      {getTokenName(coin) || coin}
                    </button>
                  ))}
                </div>
              </div>
              <div
                className="rounded-lg p-3 text-xs"
                style={{ background: C.card, border: `1px solid ${C.border}` }}
              >
                <div className="flex justify-between items-center">
                  <span style={{ color: C.dim }}>
                    {t("profilePage.transferDialog.currentBalance", {
                      tokenName: tokenLabel,
                    })}
                  </span>
                  <span
                    className="mono-num font-semibold"
                    style={{ color: C.text }}
                  >
                    {currentBalance} {tokenLabel}
                  </span>
                </div>
              </div>
              {requiresReceiverTeamWarn && (
                <div
                  className="rounded-lg p-3 text-xs flex items-start gap-1.5"
                  style={{
                    background: "rgba(245,158,11,0.06)",
                    border: "1px solid rgba(245,158,11,0.20)",
                    color: C.amber,
                  }}
                >
                  <Info size={11} className="mt-0.5 shrink-0" />
                  <span>{getInfoText(selectedCoin)}</span>
                </div>
              )}
              <div>
                <div className="flex justify-between items-center">
                  <span className="text-xs mb-2" style={{ color: C.dim }}>
                    {t("profilePage.transferDialog.transferAmount")}
                  </span>
                  <span className="text-xs mb-2" style={{ color: C.dim }}>
                    {t("profilePage.transferDialog.transferableBalance", {
                      value: getTransferableBalance(selectedCoin),
                      tokenName: tokenLabel,
                    })}
                  </span>
                </div>
                <div className="relative">
                  <input
                    type="text"
                    inputMode="decimal"
                    value={amount}
                    onChange={handleAmountChange}
                    placeholder={t(
                      "profilePage.transferDialog.amountPlaceholder"
                    )}
                    className="w-full px-3 py-3 rounded-lg text-sm mono-num outline-none"
                    style={{
                      background: C.card,
                      border: `1px solid ${C.border}`,
                      color: C.text,
                    }}
                  />
                  <span
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-xs"
                    style={{ color: C.dim }}
                  >
                    {tokenLabel}
                  </span>
                </div>
              </div>

              <div>
                <p className="text-xs mb-2" style={{ color: C.dim }}>
                  {t("profilePage.transferDialog.recipientLabel")}
                </p>
                <input
                  value={toAddr}
                  onChange={e => setToAddr(e.target.value)}
                  placeholder={
                    requiresReceiverTeamWarn
                      ? t("profilePage.transferDialog.recipientPlaceholder")
                      : t("profilePage.transferDialog.addressPlaceholder")
                  }
                  className="w-full px-3 py-3 rounded-lg text-sm mono-num outline-none"
                  style={{
                    background: C.card,
                    border: `1px solid ${C.border}`,
                    color: C.text,
                  }}
                />
              </div>

              <div
                className="rounded-lg p-3 text-xs space-y-1.5"
                style={{
                  background: C.card,
                  border: `1px solid ${C.border}`,
                }}
              >
                <div className="flex justify-between">
                  <span style={{ color: C.dim }}>
                    {t("profilePage.transferDialog.feeLabel")}
                    <InfoTip
                      text={t("profilePage.transferDialog.feeTip", {
                        tokenName: tokenLabel,
                      })}
                    />
                  </span>
                  <span className="mono-num" style={{ color: C.amber }}>
                    {fee.toFixed(2)} {tokenLabel}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span style={{ color: C.dim }}>
                    {t("profilePage.transferDialog.actualReceived")}
                  </span>
                  <span className="mono-num" style={{ color: C.text }}>
                    {receivedAmount.toFixed(2)} {tokenLabel}
                  </span>
                </div>
              </div>

              <button
                type="button"
                className="w-full py-3 rounded-lg font-bold text-sm btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={confirmDisabled}
                onClick={() => void handleConfirm()}
              >
                {t("profilePage.transferDialog.confirm")}
              </button>
            </motion.div>
          )}
          {step === "pending" && (
            <motion.div
              key="pending"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex flex-col items-center py-8 gap-3"
            >
              <RefreshCw
                size={28}
                className="animate-spin"
                style={{ color: C.primary }}
              />
              <p className="text-sm" style={{ color: C.muted }}>
                {t("profilePage.transferDialog.processing")}
              </p>
            </motion.div>
          )}
          {step === "success" && (
            <motion.div
              key="success"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="flex flex-col items-center py-6 gap-3"
            >
              <CheckCircle size={40} style={{ color: C.green }} />
              <p className="font-semibold">
                {t("profilePage.transferDialog.success")}
              </p>
              <button
                type="button"
                className="w-full py-3 rounded-lg font-bold text-sm btn-primary"
                onClick={handleClose}
              >
                {t("profilePage.transferDialog.done")}
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
}
